import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Check, RotateCcw, X } from 'lucide-react';
import { GUARD_OUTCOMES, INBOUND_MAIL_STATUSES, completeInboundMail, getInboundMail, listInboundMails, reprocessInboundMail, type InboundMail as InboundMailSchema } from '@mkcore/platform/schema';
import type { z as zz } from '@mkcore/shared';
import { Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { mailboxLabel, mailboxTone } from '@/lib/mailbox-tag';
import { useActionMutation } from '@/lib/mutation';
import { useCan } from '@/lib/session';

type Mail = zz.output<typeof InboundMailSchema>;
type Status = (typeof INBOUND_MAIL_STATUSES)[number];
type View = 'text' | 'markdown' | 'html' | 'raw';
const search = z.object({
  mailboxId: z.string().uuid().optional(),
  status: z.enum(INBOUND_MAIL_STATUSES).optional(),
  guard: z.enum(GUARD_OUTCOMES).optional(),
  sender: z.string().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/admin/inbound-mails')({ validateSearch: search, component: InboundMails });

const mailsQuery = (q: Search) =>
  infiniteQueryOptions({
    queryKey: ['admin', 'inboundMail', 'list', q],
    queryFn: ({ pageParam }) =>
      call(listInboundMails, {
        ...(q.mailboxId && { mailboxId: q.mailboxId }),
        ...(q.status && { status: [q.status] }),
        ...(q.guard && { guard: [q.guard] }),
        ...(q.sender && { sender: q.sender }),
        ...(pageParam && { cursor: pageParam }),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 15_000,
  });
const mailQuery = (id: string, view: View) => queryOptions({ queryKey: ['admin', 'inboundMail', id, view], queryFn: () => call(getInboundMail, { id, view }) });

const statusTone = (s: Status) => (s === 'processed' ? 'success' : s === 'failed' ? 'danger' : s === 'filtered' ? 'outline' : s === 'routed' ? 'info' : 'warning');
const guardTone = { ok: 'success', review: 'warning', block: 'danger' } as const;

/** The processing log of incoming mail (CO-0974): per message the sender, status, guardrails and outcome. */
function InboundMails() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useInfiniteQuery(mailsQuery(s));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState<Mail | null>(null);
  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('inboundMails.title')} description={t('inboundMails.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('inboundMails.sender')} className="w-56">
          {(p) => <Input {...p} value={s.sender ?? ''} onChange={(e) => set({ sender: e.target.value || undefined })} />}
        </Field>
        <Field label={t('common.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as Status | undefined })}>
              <option value="">{t('common.all')}</option>
              {INBOUND_MAIL_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(`inboundMails.status.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('inboundMails.guard')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.guard ?? ''} onChange={(e) => set({ guard: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {GUARD_OUTCOMES.map((g) => (
                <option key={g} value={g}>
                  {t(`inboundMails.guards.${g}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('inboundMails.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('inboundMails.received')}</Th>
              <Th>{t('inboundMails.mailbox')}</Th>
              <Th>{t('inboundMails.sender')}</Th>
              <Th>{t('inboundMails.subject')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden lg:table-cell">{t('inboundMails.outcome')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((m) => (
              <Tr key={m.id}>
                <Td className="tabular whitespace-nowrap">{formatDateTime(m.receivedAt)}</Td>
                <Td>
                  <Badge tone={mailboxTone(m.mailboxAddress)} title={m.mailboxAddress}>
                    {mailboxLabel(m.mailboxAddress)}
                  </Badge>
                </Td>
                <Td>{m.fromName ?? m.fromAddress ?? '–'}</Td>
                <Td>
                  <button type="button" className="text-left font-bold hover:underline" onClick={() => setOpen(m)}>
                    {m.subject || t('inboundMails.noSubject')}
                  </button>
                  {m.attachments.length > 0 && <span className="ml-1 text-xs text-fg-3">{t('inboundMails.attachmentCount', { count: m.attachments.length })}</span>}
                </Td>
                <Td>
                  <span className="flex flex-wrap gap-1">
                    <Badge tone={statusTone(m.status)}>{t(`inboundMails.status.${m.status}`)}</Badge>
                    {m.guard && m.guard !== 'ok' && <Badge tone={guardTone[m.guard]}>{t(`inboundMails.guards.${m.guard}`)}</Badge>}
                  </span>
                </Td>
                <Td className="hidden text-fg-2 lg:table-cell">{m.outcome ?? m.error ?? '–'}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      {open && <MailDialog mail={open} onClose={() => setOpen(null)} />}
    </>
  );
}

/** One message: text, the HTML in a sandbox (nothing runs), or the MIME source; the attachments; and handling it. */
function MailDialog({ mail, onClose }: { mail: Mail; onClose: () => void }) {
  const { t } = useTranslation();
  const canUpdate = useCan('mail:update');
  const [view, setView] = useState<View>('text');
  const q = useQuery(mailQuery(mail.id, view));
  const m = q.data ?? mail;
  const [outcome, setOutcome] = useState('');
  const complete = useActionMutation(completeInboundMail, { invalidate: [['admin', 'inboundMail'], ['admin', 'mailbox']], onSuccess: onClose });
  const reprocess = useActionMutation(reprocessInboundMail, { invalidate: [['admin', 'inboundMail']], success: t('inboundMails.reprocessed') });
  const open = !m.purged && m.status !== 'filtered';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{mail.subject || t('inboundMails.noSubject')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 text-sm">
          <p className="text-fg-2">
            {t('inboundMails.fromAt', { from: mail.fromName ? `${mail.fromName} <${mail.fromAddress}>` : (mail.fromAddress ?? '–'), at: formatDateTime(mail.receivedAt), mailbox: mail.mailboxAddress })}
          </p>
          {mail.guardReasons.length > 0 && (
            <p role="note" className="text-warning">
              {t('inboundMails.guardReasons', { reasons: mail.guardReasons.join('; ') })}
            </p>
          )}
          {open && (
            <div role="tablist" aria-label={t('inboundMails.view')} className="flex gap-1">
              {(['text', 'markdown', 'html', 'raw'] as const).map((v) => (
                <Button key={v} role="tab" aria-selected={view === v} size="sm" variant={view === v ? 'primary' : 'ghost'} onClick={() => setView(v)}>
                  {t(`inboundMails.views.${v}`)}
                </Button>
              ))}
            </div>
          )}
          {!open ? (
            <p className="text-fg-3">{mail.purged ? t('inboundMails.purged') : t('inboundMails.filteredText')}</p>
          ) : !q.data ? (
            <Skeleton className="h-40" />
          ) : q.data.content === null ? (
            <p className="text-fg-3">{t('inboundMails.noContent')}</p>
          ) : view === 'html' ? (
            <iframe title={t('inboundMails.views.html')} sandbox="" srcDoc={q.data.content} className="h-80 w-full rounded-md border border-line bg-white" />
          ) : (
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-sunken p-3 text-xs">{q.data.content}</pre>
          )}
          {m.attachments.length > 0 && (
            <ul aria-label={t('inboundMails.attachments')} className="grid gap-1">
              {m.attachments.map((a) => (
                <li key={a.graphId}>
                  {a.fileId ? (
                    <a href={`/api/v1/files/${a.fileId}/content`} target="_blank" rel="noreferrer" className="underline">
                      {a.name}
                    </a>
                  ) : (
                    <span>
                      {a.name} <span className="text-fg-3">({t('inboundMails.notStored')})</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canUpdate && open && m.status !== 'processed' && (
            <Field label={t('inboundMails.outcome')}>{(p) => <Input {...p} value={outcome} onChange={(e) => setOutcome(e.target.value)} />}</Field>
          )}
        </div>
        <DialogFooter>
          {canUpdate && open && m.guard !== 'block' && (
            <Button variant="secondary" loading={reprocess.isPending} onClick={() => reprocess.mutate({ input: { id: mail.id } })}>
              <RotateCcw /> {mail.guard === 'review' ? t('inboundMails.release') : t('inboundMails.reprocess')}
            </Button>
          )}
          {canUpdate && open && m.status !== 'processed' && (
            <>
              <Button variant="danger" loading={complete.isPending} onClick={() => complete.mutate({ input: { id: mail.id, status: 'failed', ...(outcome.trim() && { outcome: outcome.trim() }) } })}>
                <X /> {t('inboundMails.markFailed')}
              </Button>
              <Button loading={complete.isPending} onClick={() => complete.mutate({ input: { id: mail.id, status: 'processed', ...(outcome.trim() && { outcome: outcome.trim() }) } })}>
                <Check /> {t('inboundMails.markProcessed')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
